import React, { useState, useEffect, useMemo } from 'react';
import {
  TaskyAlert,
  TaskyButton,
  TaskyFormGroup,
  TaskyInput,
  TaskyLabel,
  TaskySelect,
  TaskySpinner,
  TaskySwitch,
  TaskyTextarea,
} from '../tasky';
import { AIAgent, ModelType, AIModelsResponse, Category } from '../../types';
import aiAgentService, { CreateAgentRequest, UpdateAgentRequest } from '../../services/api/aiAgentService';
import AIModelService from '../../services/api/aiModelService';
import categoryService from '../../services/api/categoryService';

interface AIAgentFormProps {
  agent?: AIAgent;
  onSubmit?: (agentData: any) => void;
  onCancel?: () => void;
}

// デフォルトの色オプション
const DEFAULT_COLORS = [
  { color: '#FFC107', name: 'イエロー' },
  { color: '#FF9800', name: 'オレンジ' },
  { color: '#F44336', name: 'レッド' },
  { color: '#E91E63', name: 'ピンク' },
  { color: '#9C27B0', name: 'パープル' },
  { color: '#673AB7', name: 'ディープパープル' },
  { color: '#3F51B5', name: 'インディゴ' },
  { color: '#2196F3', name: 'ブルー' },
  { color: '#03A9F4', name: 'ライトブルー' },
  { color: '#00BCD4', name: 'シアン' },
  { color: '#009688', name: 'ティール' },
  { color: '#4CAF50', name: 'グリーン' },
  { color: '#8BC34A', name: 'ライトグリーン' },
  { color: '#CDDC39', name: 'ライム' },
];

// フォールバック用のデフォルトモデルオプション
const FALLBACK_MODEL_OPTIONS = [
  { value: 'chatgpt', label: 'ChatGPT (OpenAI)' },
  { value: 'claude', label: 'Claude (Anthropic)' },
  { value: 'gemini', label: 'Gemini (Google)' },
];

const AIAgentForm: React.FC<AIAgentFormProps> = ({
  agent,
  onSubmit,
  onCancel,
}) => {
  // フォームの状態
  const [title, setTitle] = useState(agent?.title || '');
  const [description, setDescription] = useState(agent?.description || '');
  const [modelType, setModelType] = useState<ModelType | ''>(agent?.model_type || '');
  const [promptTemplate, setPromptTemplate] = useState(agent?.prompt_template || '');
  const [isActive, setIsActive] = useState(agent?.is_active ?? true);
  const [categoryId, setCategoryId] = useState<number | ''>(agent?.category_id || '');
  const [baseColor, setBaseColor] = useState(agent?.base_color || '#FFC107');
  const [quickReplies, setQuickReplies] = useState<string[]>(agent?.quick_replies || []);

  // AIモデル関連の状態
  const [modelsData, setModelsData] = useState<AIModelsResponse | null>(null);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState<string | null>(null);

  // カテゴリ関連の状態
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  // エラーと成功メッセージの状態
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 編集モードか新規作成モードかを判定
  const isEditMode = !!agent;

  // AIモデル一覧の取得
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setModelsLoading(true);
        setModelsError(null);
        // キャッシュをクリアして最新データを強制取得
        AIModelService.clearCache();
        const data = await AIModelService.getModelsWithCache(true);
        console.log('取得したモデルデータ:', data);
        setModelsData(data);
      } catch (error) {
        console.error('モデル一覧の取得に失敗しました:', error);
        setModelsError('モデル一覧の取得に失敗しました');
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  }, []);

  // カテゴリ一覧の取得
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        setCategoriesError(null);
        const data = await categoryService.getAllCategories();
        setCategories(data);
      } catch (error) {
        console.error('カテゴリ一覧の取得に失敗しました:', error);
        setCategoriesError('カテゴリ一覧の取得に失敗しました');
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // モデルオプションの生成
  const modelOptions = useMemo(() => {
    if (!modelsData?.success || !modelsData.data?.categories) {
      return FALLBACK_MODEL_OPTIONS;
    }

    const options: Array<{ value: string; label: string }> = [];

    // カテゴリ別にモデルを追加
    Object.entries(modelsData.data.categories).forEach(([categoryKey, category]) => {
      if (category.models && category.models.length > 0) {
        // カテゴリ内のモデルを追加
        category.models.forEach(model => {
          options.push({
            value: model.type,
            label: `${model.display_name} - ${model.description}`,
          });
        });
      }
    });

    return options.length > 0 ? options : FALLBACK_MODEL_OPTIONS;
  }, [modelsData]);

  // カテゴリオプションの生成
  const categoryOptions = useMemo(() => {
    if (categoriesLoading || categories.length === 0) {
      return [{ value: '', label: 'カテゴリを選択してください' }];
    }

    return [
      { value: '', label: 'カテゴリを選択してください' },
      ...categories
        .filter(category => category.is_active)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(category => ({
          value: category.id.toString(),
          label: category.name
        }))
    ];
  }, [categories, categoriesLoading]);

  // クイックリプライの管理関数
  const updateQuickReply = (index: number, value: string) => {
    const newReplies = [...quickReplies];
    newReplies[index] = value;
    setQuickReplies(newReplies);
  };

  const addQuickReply = () => {
    if (quickReplies.length < 4) {
      setQuickReplies([...quickReplies, '']);
    }
  };

  const removeQuickReply = (index: number) => {
    const newReplies = quickReplies.filter((_, i) => i !== index);
    setQuickReplies(newReplies);
  };

  // バリデーション関数
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'タイトルは必須です';
    }

    if (!modelType) {
      newErrors.modelType = 'AIサービスを選択してください';
    }

    if (!promptTemplate.trim()) {
      newErrors.promptTemplate = 'プロンプトテンプレートは必須です';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // フォーム送信ハンドラー
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // バリデーション
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setFormError(null);
      setFormSuccess(null);

      // 送信データ準備
      const agentData = {
        title,
        description: description || undefined,
        model_type: modelType as ModelType,
        prompt_template: promptTemplate,
        is_active: isActive,
        category_id: categoryId ? Number(categoryId) : null,
        base_color: baseColor,
        quick_replies: quickReplies.filter(reply => reply.trim() !== '') // 空でないものだけ含める
      };

      // 親コンポーネントのonSubmitを呼び出す（APIコールは親で行う）
      if (onSubmit) {
        await onSubmit(agentData);

        // 成功メッセージを表示
        if (isEditMode) {
          setFormSuccess('AIエージェントが正常に更新されました');
        } else {
          setFormSuccess('AIエージェントが正常に作成されました');

          // 新規作成後はフォームをリセット
          setTitle('');
          setDescription('');
          setModelType('');
          setPromptTemplate('');
          setIsActive(true);
          setCategoryId('');
          setBaseColor('#FFC107');
          setQuickReplies([]);
        }
      }
    } catch (error) {
      console.error('AIエージェントの保存に失敗しました:', error);
      setFormError('AIエージェントの保存に失敗しました。後でもう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-tasky-lg p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-dark mb-1">
          {isEditMode ? 'AIエージェントを編集' : '新規AIエージェント作成'}
        </h2>
        <p className="text-sm text-gray-500">
          AIエージェントの設定をカスタマイズします。プロンプトテンプレートはAIの応答スタイルを決定します。
        </p>
      </div>

      {formError && <TaskyAlert tone="error" className="mb-4">{formError}</TaskyAlert>}
      {formSuccess && <TaskyAlert tone="success" className="mb-4">{formSuccess}</TaskyAlert>}
      {modelsError && <TaskyAlert tone="error" className="mb-4">{modelsError}</TaskyAlert>}

      <form onSubmit={handleSubmit} className="space-y-5">
        <TaskyFormGroup
          label="エージェント名"
          required
          htmlFor="agent-title"
          error={errors.title}
        >
          <TaskyInput
            id="agent-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="例: カスタマーサポートアシスタント"
            required
            hasError={!!errors.title}
          />
        </TaskyFormGroup>

        <TaskyFormGroup label="説明" htmlFor="agent-description">
          <TaskyInput
            id="agent-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="例: ユーザーの質問に回答し、サポート情報を提供するアシスタント"
          />
        </TaskyFormGroup>

        {modelsLoading ? (
          <div className="flex items-center justify-center gap-2 p-4 text-sm text-gray-500">
            <TaskySpinner size="sm" />
            <span>モデル一覧を読み込み中...</span>
          </div>
        ) : (
          <TaskyFormGroup
            label="AIモデル"
            required
            htmlFor="agent-model-type"
            error={errors.modelType}
          >
            <TaskySelect
              id="agent-model-type"
              value={modelType}
              onChange={(e) => {
                const value = e.target.value;
                setModelType(value as ModelType);
              }}
              required
              hasError={!!errors.modelType}
            >
              <option value="" disabled>選択してください</option>
              {modelOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TaskySelect>
          </TaskyFormGroup>
        )}

        <TaskyFormGroup
          label="プロンプトテンプレート"
          required
          htmlFor="agent-prompt-template"
          error={errors.promptTemplate}
        >
          <TaskyTextarea
            id="agent-prompt-template"
            value={promptTemplate}
            onChange={(e) => setPromptTemplate(e.target.value)}
            placeholder="あなたは[会社名]のカスタマーサポートアシスタントです。以下のガイドラインに従って応答してください..."
            required
            rows={8}
            hasError={!!errors.promptTemplate}
          />
        </TaskyFormGroup>

        <TaskyFormGroup label="カテゴリ" htmlFor="agent-category">
          <TaskySelect
            id="agent-category"
            value={categoryId.toString()}
            onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="" disabled>選択してください</option>
            {categoryOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TaskySelect>
        </TaskyFormGroup>

        <div>
          <TaskyLabel>基準色 (サイドバーアイコン用)</TaskyLabel>
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-full shadow-sm shrink-0"
              style={{ background: `linear-gradient(135deg, ${baseColor}, ${baseColor}40)` }}
            />
            <div>
              <div className="text-sm text-dark">
                {DEFAULT_COLORS.find(c => c.color === baseColor)?.name || '選択した色'}
              </div>
              <div className="text-xs text-gray-500">{baseColor}</div>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-2 mt-2">
            {DEFAULT_COLORS.map((colorOption) => (
              <button
                type="button"
                key={colorOption.color}
                onClick={() => setBaseColor(colorOption.color)}
                title={colorOption.name}
                aria-label={colorOption.name}
                className={`w-8 h-8 rounded-full cursor-pointer border-2 transition-transform hover:scale-110 ${
                  baseColor === colorOption.color ? 'border-dark' : 'border-transparent'
                }`}
                style={{ backgroundColor: colorOption.color }}
              />
            ))}
          </div>
        </div>

        <div>
          <TaskyLabel>初回クイックリプライ（最大4つ）</TaskyLabel>
          <p className="text-xs text-gray-500 mb-3">
            チャット開始時にユーザーに表示される提案メッセージです。設定しない場合は表示されません。
          </p>

          {quickReplies.map((reply, index) => (
            <div key={index} className="flex items-center gap-2 mb-2">
              <TaskyInput
                type="text"
                value={reply}
                onChange={(e) => updateQuickReply(index, e.target.value)}
                placeholder={`クイックリプライ ${index + 1}`}
                maxLength={50}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => removeQuickReply(index)}
                className="shrink-0 px-3 py-2 text-xs text-gray-600 bg-white border border-warm-border rounded-tasky-sm hover:bg-warm-hover hover:text-dark transition-colors"
              >
                削除
              </button>
            </div>
          ))}

          {quickReplies.length < 4 && (
            <button
              type="button"
              onClick={addQuickReply}
              className="mt-1 px-3 py-2 text-xs text-gray-600 bg-white border border-warm-border rounded-tasky-sm hover:bg-warm-hover hover:text-dark transition-colors"
            >
              + クイックリプライを追加
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-dark">ステータス</span>
          <TaskySwitch
            id="agent-is-active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            label={
              <span className={`text-xs font-medium ${isActive ? 'text-success' : 'text-destructive'}`}>
                {isActive ? '有効' : '無効'}
              </span>
            }
          />
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-warm-border">
          {onCancel && (
            <TaskyButton type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
              キャンセル
            </TaskyButton>
          )}
          <TaskyButton type="submit" size="sm" disabled={isSubmitting || modelsLoading}>
            {isSubmitting ? '保存中...' : isEditMode ? '更新する' : '作成する'}
          </TaskyButton>
        </div>
      </form>
    </div>
  );
};

export default AIAgentForm;
